import { useEffect, useRef, useState } from 'react'
import { fontLoader } from '../../infrastructure/services/fontLoaderService'
import { useCanvasStore } from '../../stores/canvasStore'
import { useQueryFonts } from './useQueryFonts'

/**
 * Hook to preload fonts used in the current design
 */
export const useFontPreloader = () => {
  const elements = useCanvasStore((state) => state.elements)
  const setLoadingState = useCanvasStore((state) => state.setLoadingState)
  const fonts = useQueryFonts()
  const [fontsLoaded, setFontsLoaded] = useState(false)
  const loadedFontsRef = useRef<Set<string>>(new Set())

  // Force initial render when fonts are ready
  useEffect(() => {
    document.fonts.ready.then(() => {
      const { forceUpdate, setLoadingState } = useCanvasStore.getState()
      if (forceUpdate) {
        forceUpdate()
      }
      // Mark fonts as loaded if no custom fonts are used
      setLoadingState({ fontsLoaded: true })
    })
  }, [])

  useEffect(() => {
    if (!fonts || fonts.length === 0) return

    // Get unique font families used in text elements
    const usedFontFamilies = new Set<string>()

    Object.values(elements).forEach((element) => {
      if (element.type === 'text' && element.fontFamily) {
        usedFontFamilies.add(element.fontFamily)
      }
    })

    if (usedFontFamilies.size === 0) return

    // Find font URLs for used fonts
    const fontsToLoad: Array<{ name: string; url: string; weight?: number; italic?: boolean }> = []

    // Check each element for font usage
    Object.values(elements).forEach((element) => {
      if (element.type === 'text' && element.fontFamily) {
        // Create a unique key for this font configuration
        const fontKey = `${element.fontFamily}-${element.fontWeight || 400}-${element.fontItalic || false}`

        // Skip if we've already loaded this font
        if (loadedFontsRef.current.has(fontKey)) {
          return
        }

        // Find matching font with weight and italic
        const matchingFont = fonts.find(
          (f) =>
            f.name === element.fontFamily &&
            f.weight === (element.fontWeight || 400) &&
            f.italic === (element.fontItalic || false),
        )

        if (matchingFont) {
          // Check if we haven't already added this exact font to load
          const alreadyAdded = fontsToLoad.some(
            (f) =>
              f.name === matchingFont.name &&
              f.weight === matchingFont.weight &&
              f.italic === matchingFont.italic,
          )

          if (!alreadyAdded) {
            fontsToLoad.push({
              name: matchingFont.name,
              url: matchingFont.url,
              weight: matchingFont.weight,
              italic: matchingFont.italic,
            })
          }
        }
      }
    })

    // Preload the fonts only if there are new fonts to load
    if (fontsToLoad.length > 0) {
      setFontsLoaded(false)

      // Mark these fonts as being loaded
      fontsToLoad.forEach((font) => {
        const fontKey = `${font.name}-${font.weight || 400}-${font.italic || false}`
        loadedFontsRef.current.add(fontKey)
      })

      fontLoader.preloadFonts(fontsToLoad).then(async () => {
        // Wait for all fonts to be fully loaded and ready
        await document.fonts.ready

        // Successfully preloaded fonts
        setFontsLoaded(true)
        // Only force update once after loading new fonts
        const { forceUpdate, setLoadingState } = useCanvasStore.getState()
        if (forceUpdate) {
          forceUpdate()
        }
        // Notify canvas store that fonts are loaded
        setLoadingState({ fontsLoaded: true })
      })
    } else if (fontsToLoad.length === 0) {
      setFontsLoaded(true)
      // No fonts to load, mark as loaded
      setLoadingState({ fontsLoaded: true })
    }
  }, [elements, fonts])

  return { fontsLoaded }
}
